// The reference lists Admins edit in P1.1, described once: the broker validates with the zod
// schemas built from these definitions, and the Admin screen draws its forms from them. Rows are
// never deleted (additive data rule); lists with an `Active` column are switched off instead.

import { z } from 'zod';
import type { ListBase } from '../data-model/lists';
import { ENTITIES } from '../rules/numbering';

export type FieldKind = 'text' | 'note' | 'email' | 'enum' | 'bool' | 'number';

export interface FieldDef {
  name: string;
  kind: FieldKind;
  required?: true;
  /** Set when the row is created, read-only afterwards (the key every other list points at). */
  key?: true;
  max?: number;
  options?: readonly string[];
  pattern?: RegExp;
  min?: number;
  maxValue?: number;
  /** Must match an existing row of another list (checked by the broker). */
  ref?: { list: ListBase; field: string };
}

export interface AdminListDef {
  fields: readonly FieldDef[];
  /** False for fixed lists (Entities, Offices): their rows come from provisioning only. */
  canCreate: boolean;
  /** The list has ChangedBy / ChangedAt columns the broker fills in. */
  tracksChange: boolean;
}

const CODE3 = /^[A-Z]{3}$/;
const UPN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export const ADMIN_LISTS = {
  VerticalEntityMap: {
    canCreate: true,
    tracksChange: true,
    fields: [
      { name: 'Vertical', kind: 'text', required: true, key: true, max: 80 },
      { name: 'Entity', kind: 'enum', required: true, options: ENTITIES },
      // D114: PO approver after the CFO for this vertical; blank = no extra step.
      { name: 'POApproverUPN', kind: 'email', max: 120 },
      { name: 'POApproverBackupUPN', kind: 'email', max: 120 },
      { name: 'Active', kind: 'bool' },
    ],
  },
  DeliveryLocations: {
    canCreate: true,
    tracksChange: false,
    fields: [
      { name: 'Location', kind: 'text', required: true, key: true, max: 80 },
      {
        name: 'Office',
        kind: 'text',
        required: true,
        pattern: CODE3,
        ref: { list: 'Offices', field: 'Code' },
      },
      { name: 'Address', kind: 'note', max: 500 },
      { name: 'Active', kind: 'bool' },
    ],
  },
  Departments: {
    canCreate: true,
    tracksChange: false,
    fields: [
      { name: 'Department', kind: 'text', required: true, key: true, max: 120 },
      { name: 'Entity', kind: 'enum', required: true, options: ENTITIES },
      {
        name: 'Office',
        kind: 'text',
        required: true,
        pattern: CODE3,
        ref: { list: 'Offices', field: 'Code' },
      },
      { name: 'HeadUPN', kind: 'email', required: true, max: 120 },
      { name: 'BackupUPN', kind: 'email', max: 120 },
      { name: 'Active', kind: 'bool' },
    ],
  },
  Categories: {
    canCreate: true,
    tracksChange: false,
    fields: [
      {
        name: 'CategoryId',
        kind: 'text',
        required: true,
        key: true,
        max: 30,
        pattern: /^[A-Z0-9][A-Z0-9-]{1,29}$/,
      },
      { name: 'NameEN', kind: 'text', required: true, max: 120 },
      { name: 'NameAR', kind: 'text', required: true, max: 120 },
      { name: 'Type', kind: 'enum', required: true, options: ['goods', 'service'] },
      { name: 'Active', kind: 'bool' },
    ],
  },
  Entities: {
    canCreate: false,
    tracksChange: false,
    fields: [
      { name: 'Code', kind: 'enum', required: true, key: true, options: ENTITIES },
      { name: 'LegalNameEN', kind: 'text', max: 200 },
      { name: 'LegalNameAR', kind: 'text', max: 200 },
      { name: 'CR', kind: 'text', max: 20, pattern: /^\d{10}$/ },
      { name: 'VAT', kind: 'text', max: 20, pattern: /^\d{15}$/ },
      { name: 'Address', kind: 'note', max: 500 },
      { name: 'LogoLink', kind: 'note', max: 500, pattern: /^(https:\/\/|\/)\S+$/ },
    ],
  },
  Offices: {
    canCreate: false,
    tracksChange: false,
    fields: [
      { name: 'Code', kind: 'text', required: true, key: true, pattern: CODE3 },
      { name: 'Country', kind: 'text', required: true, max: 80 },
      { name: 'Currency', kind: 'text', required: true, pattern: CODE3 },
      { name: 'VATRate', kind: 'number', required: true, min: 0, maxValue: 100 },
      {
        name: 'TimeZone',
        kind: 'text',
        required: true,
        max: 64,
        pattern: /^[A-Za-z]+\/[A-Za-z_]+$/,
      },
      // D117: the office's own registration, printed on its POs and delivery notes when CR is set.
      // UAE licence and Egypt register numbers vary in format, so only the characters are checked.
      { name: 'LegalNameEN', kind: 'text', max: 200 },
      { name: 'LegalNameAR', kind: 'text', max: 200 },
      { name: 'CR', kind: 'text', max: 30, pattern: /^[A-Za-z0-9][A-Za-z0-9 /-]{2,29}$/ },
      { name: 'VAT', kind: 'text', max: 20, pattern: /^\d{9,15}$/ },
      { name: 'Address', kind: 'note', max: 500 },
    ],
  },
} as const satisfies Record<string, AdminListDef>;

export type AdminListName = keyof typeof ADMIN_LISTS;
export const ADMIN_LIST_NAMES = Object.keys(ADMIN_LISTS) as AdminListName[];

export function isAdminList(name: string): name is AdminListName {
  return Object.prototype.hasOwnProperty.call(ADMIN_LISTS, name);
}

export function listDef(name: AdminListName): AdminListDef {
  return ADMIN_LISTS[name];
}

export function keyField(name: AdminListName): FieldDef {
  const key = listDef(name).fields.find((f) => f.key);
  if (!key) throw new Error(`${name} has no key field`);
  return key;
}

function fieldSchema(f: FieldDef): z.ZodType {
  switch (f.kind) {
    case 'bool':
      return z.boolean();
    case 'number': {
      let n = z.number().finite();
      if (f.min !== undefined) n = n.min(f.min);
      if (f.maxValue !== undefined) n = n.max(f.maxValue);
      return n;
    }
    case 'enum':
      return z.enum(f.options as [string, ...string[]]);
    default: {
      let s = z.string().trim();
      if (f.max) s = s.max(f.max);
      if (f.kind === 'email') s = s.toLowerCase().regex(UPN, 'Enter a work email address');
      if (f.pattern) s = s.regex(f.pattern);
      // Free text is stored as plain text and shown escaped; angle brackets are refused so no
      // markup ever reaches a document template (03-SECURITY "No HTML in free text").
      if (f.kind === 'text' || f.kind === 'note') s = s.regex(/^[^<>]*$/, 'Remove < and >');
      return s;
    }
  }
}

/** Accepts a blank optional text field as "clear it"; required fields must have a value. */
function withPresence(f: FieldDef, schema: z.ZodType): z.ZodType {
  if (f.required) {
    return f.kind === 'text' || f.kind === 'email' || f.kind === 'note'
      ? (schema as z.ZodString).min(1, 'Required')
      : schema;
  }
  if (f.kind === 'bool' || f.kind === 'number' || f.kind === 'enum') return schema.optional();
  return z.union([z.literal(''), schema]).optional();
}

/** Schema for a new row: every required field, keys included. Unknown fields are refused. */
export function createSchema(name: AdminListName) {
  const shape: Record<string, z.ZodType> = {};
  for (const f of listDef(name).fields) shape[f.name] = withPresence(f, fieldSchema(f));
  return z.strictObject(shape);
}

/** Schema for an edit: keys cannot change; any other field may be sent alone. */
export function updateSchema(name: AdminListName) {
  const shape: Record<string, z.ZodType> = {};
  for (const f of listDef(name).fields) {
    if (f.key) continue;
    const s = withPresence(f, fieldSchema(f));
    shape[f.name] = s.optional();
  }
  return z.strictObject(shape).refine((v) => Object.keys(v).length > 0, 'Nothing to change');
}

/** Cross-field rules the schemas cannot express. Returns a message, or null when fine. */
export function crossCheck(name: AdminListName, row: Record<string, unknown>): string | null {
  if (name === 'Departments') {
    const head = String(row.HeadUPN ?? '').toLowerCase();
    const backup = String(row.BackupUPN ?? '').toLowerCase();
    if (head && backup && head === backup) return 'Head and backup must be different people';
  }
  return null;
}

export interface AdminRow {
  id: string;
  etag: string;
  fields: Record<string, unknown>;
}
